import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn, Icon, Skeleton } from "@nexus/ui";
import type { EventJobStats } from "@/features/events/api/events.api";
import type { EventTimeRange } from "../constants/event-time-ranges";
import { formatPreciseDurationMs } from "../lib/event-timing";
import { EventsTimeRangeControl } from "./EventsTimeRangeControl";

interface EventsKpiStripProps {
  stats: EventJobStats | undefined;
  isLoading: boolean;
  /** These numbers still belong to the previous window; the new one is in flight. */
  isStale: boolean;
  /** Scopes this strip, the activity chart and the table alike. */
  timeRange: EventTimeRange;
  onTimeRangeChange: (value: EventTimeRange) => void;
  /** Highlights the Failed stat while the status filter is already FAILED. */
  isFailedFilterActive: boolean;
  onFilterFailed: () => void;
}

const LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";
const VALUE_CLASS = "text-lg font-semibold leading-7 tabular-nums";
/** A zero is the healthy answer, so it reports quietly and lets a real number carry the weight. */
const ZERO_VALUE_CLASS = "text-lg font-normal leading-7 tabular-nums text-muted-foreground";
const SECONDARY_CLASS = "text-[11px] text-muted-foreground";
/** Padding matches the Failed button so every stat sits on the same baseline. */
const STAT_CLASS = "flex flex-col gap-0.5 p-1.5 text-left";
/** Rough width of each stat, so the loading state does not jump on arrival. */
const SKELETON_WIDTHS = ["w-16", "w-20", "w-28", "w-28"];

function formatCount(value: number, locale: string): string {
  return value.toLocaleString(locale);
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  return formatPreciseDurationMs(ms);
}

function formatFailureRate(failed: number, total: number, locale: string): string | null {
  if (total <= 0 || failed <= 0) return null;
  return `${((failed / total) * 100).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={STAT_CLASS}>
      <span className={LABEL_CLASS}>{label}</span>
      <span className="flex items-baseline gap-1.5">{children}</span>
    </div>
  );
}

export function EventsKpiStrip({
  stats,
  isLoading,
  isStale,
  timeRange,
  onTimeRangeChange,
  isFailedFilterActive,
  onFilterFailed,
}: EventsKpiStripProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // The control is not stats data, so it stays live while the numbers it scopes
  // are still loading — otherwise switching window disables the only way back.
  const rangeControl = (
    <div className="ml-1.5 shrink-0">
      <EventsTimeRangeControl value={timeRange} onChange={onTimeRangeChange} />
    </div>
  );

  if (isLoading || !stats) {
    return (
      // Mirrors the resolved layout — window control, three aggregates, then the
      // live gauge on the right — so nothing shifts when the numbers land.
      <div className="-mx-1.5 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3">
        {rangeControl}
        {SKELETON_WIDTHS.map((width, index) => (
          // Skeleton's default bg-muted is the same value as the surface it
          // lands on here, so the placeholders need the ink wash to show up.
          <Skeleton
            key={index}
            className={cn(
              "h-11 rounded-sm bg-foreground/8",
              width,
              index === SKELETON_WIDTHS.length - 1 && "ml-auto",
            )}
          />
        ))}
      </div>
    );
  }

  const failureRate = formatFailureRate(stats.failed, stats.total, locale);

  const hasFailures = stats.failed > 0;
  const isBusy = stats.processingNow > 0;
  // Filtering to failures with none to show is a dead end, so the stat only
  // acts as a button when there is something behind it — or when it is already
  // the active filter and the admin needs a way back out.
  const canFilterFailed = hasFailures || isFailedFilterActive;

  return (
    // Five bordered cards for five numbers pushed the table below the fold on a
    // laptop. The stats sit straight on the page instead, reading quiet while
    // everything is healthy so that a real number is the thing that draws the
    // eye — the only stat that takes a surface is the one you can click.
    <div
      className={cn(
        "-mx-1.5 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3",
        "[&>*]:transition-opacity [&>*]:duration-200",
        // Everything after the control is a number measured over the window the
        // admin just left, so it reads as provisional until the new one lands.
        // The control itself stays solid — it is showing the new window truly.
        isStale && "[&>*:not(:first-child)]:opacity-40",
      )}
      aria-busy={isStale || undefined}
    >
      {/* The window leads the strip rather than trailing it: everything to its
          right, and everything below it on the page, is measured over it. */}
      {rangeControl}

      <Stat label={t("admin.events.kpi.total", "Events")}>
        <span className={stats.total > 0 ? VALUE_CLASS : ZERO_VALUE_CLASS}>
          {formatCount(stats.total, locale)}
        </span>
      </Stat>

      <button
        type="button"
        onClick={onFilterFailed}
        disabled={!canFilterFailed}
        aria-pressed={isFailedFilterActive}
        title={
          canFilterFailed
            ? t("admin.events.kpi.failedHint", "Filter to failed events")
            : t("admin.events.kpi.failedNone", "No failures in this window")
        }
        className={cn(
          STAT_CLASS,
          "rounded-sm transition-colors",
          // The strip sits on --content-background, where bg-muted is invisible;
          // an ink wash is the one hover tint that reads on both themes.
          canFilterFailed ? "cursor-pointer hover:bg-foreground/5" : "cursor-default",
          isFailedFilterActive && "bg-error-subtle hover:bg-error-subtle",
        )}
      >
        <span className={LABEL_CLASS}>{t("admin.events.kpi.failed", "Failed")}</span>
        <span className="flex items-baseline gap-1.5">
          <span
            className={cn(hasFailures ? VALUE_CLASS : ZERO_VALUE_CLASS, hasFailures && "text-error")}
          >
            {formatCount(stats.failed, locale)}
          </span>
          {failureRate ? <span className={SECONDARY_CLASS}>{failureRate}</span> : null}
        </span>
      </button>

      {/* Average and p95 describe one distribution, so they read as one stat
          rather than two identical cards. */}
      <Stat label={t("admin.events.kpi.avgRun", "Avg run")}>
        <span className={stats.avgRunMs == null ? ZERO_VALUE_CLASS : VALUE_CLASS}>
          {formatDuration(stats.avgRunMs)}
        </span>
        {stats.p95RunMs != null ? (
          <span className={SECONDARY_CLASS}>
            {t("admin.events.kpi.p95Run", "p95 {{duration}}", {
              duration: formatDuration(stats.p95RunMs),
            })}
          </span>
        ) : null}
      </Stat>

      {/* A live gauge, not a 24h total — it sits apart so the window chip above
          cannot be read as covering it. */}
      <div className="ml-auto">
        <Stat label={t("admin.events.kpi.processingNow", "Processing now")}>
          <span className={isBusy ? VALUE_CLASS : ZERO_VALUE_CLASS}>
            {formatCount(stats.processingNow, locale)}
          </span>
          {isBusy ? (
            <Icon
              name="loader"
              size="xs"
              colorVar="--info"
              className="animate-spin motion-reduce:animate-none"
              aria-hidden
            />
          ) : null}
        </Stat>
      </div>
    </div>
  );
}
